// engine-exception: the About page is not a resource — it compares the build identity of this bundle (baked commit), the build the web server currently serves (/env.js) and the API's (/health), and flags a skew between them; no ScreenDef kind expresses a three-way comparison of non-resource reads
import { versionSkew } from '../../engine/shell/VersionSkewChip';
import { useState } from 'react';
import { PageHeader, Stat, useApiQuery } from '../../engine';
import { Banner, Button } from '../../engine/design';
import { useStaleBuild } from '../../engine/shell/StaleBuildBanner';
import { BUILD, commitUrl, shortSha } from '../../lib/build';
import { runtimeConfig } from '../../lib/runtimeConfig';
import { formatDateTime } from '../../lib/format';

// Which build is running where. Three answers, side by side:
//   * this tab — the commit the loaded bundle was built from (VITE_GIT_SHA at build time);
//   * the web server — the commit the web container was deployed from (/env.js, no-store);
//   * the API — GET /health: version, revision, deploy time.
// Any disagreement is the deployment problem this page exists to make visible: a tab older
// than the server (reload), or a web image newer than the API (a build that did not land).

interface Health {
  ok: boolean;
  version?: string;
  revision?: string;
  deployed_at?: string | null;
}

export interface AboutScreenProps {
  /** This bundle's identity (default: BUILD). Injectable for tests. */
  build?: { sha: string; version: string };
  served?: { sha: string; deployedAt: string | null };
}

export function AboutScreen({ build = BUILD, served = { sha: runtimeConfig.servedSha, deployedAt: runtimeConfig.deployedAt } }: AboutScreenProps) {
  const health = useApiQuery<Health>(['health'], '/health', { staleTime: 30_000 });
  const { stale } = useStaleBuild({ currentSha: build.sha });
  const [copied, setCopied] = useState(false);
  const api = health.data;
  const skew = versionSkew(build.sha, api?.revision);

  const summary = [
    `web build ${build.sha} (v${build.version})`,
    `web served ${served.sha}${served.deployedAt ? ` deployed ${served.deployedAt}` : ''}`,
    api ? `api ${api.revision ?? 'unknown'} (v${api.version ?? '?'})${api.deployed_at ? ` deployed ${api.deployed_at}` : ''}` : 'api unreachable',
  ].join('\n');

  return (
    <div className="hf-page">
      <PageHeader
        title="About"
        subtitle="Which build is running in this tab, on the web server, and on the API. All three should name the same commit."
        actions={
          <Button
            size="sm"
            onClick={() => {
              void navigator.clipboard?.writeText(summary).then(() => setCopied(true));
            }}
          >
            {copied ? 'Copied' : 'Copy details'}
          </Button>
        }
      />
      {stale && (
        <Banner tone="warn" icon="refresh-cw" action={<Button size="sm" onClick={() => window.location.reload()}>Reload</Button>}>
          This tab is running an older build than the web server now serves.
        </Banner>
      )}
      {skew && (
        <Banner tone="warn" icon="git-branch">
          The web app and the API are running different commits ({shortSha(build.sha)} vs {shortSha(api?.revision)}). One of the two deployments did not land; check the api and web services' last builds.
        </Banner>
      )}
      {health.error && <Banner tone="error">The API did not answer /health: {health.error instanceof Error ? health.error.message : String(health.error)}</Banner>}
      <div className="hf-stats">
        <Stat label="This tab" value={<Sha sha={build.sha} />} sub={`Version ${build.version}`} icon="git-branch" />
        <Stat label="Web server" value={<Sha sha={served.sha} />} sub={served.deployedAt ? `Deployed ${formatDateTime(served.deployedAt)}` : 'Deploy time unknown'} icon="git-branch" />
        <Stat
          label="API"
          value={health.isLoading ? '…' : <Sha sha={api?.revision ?? 'unknown'} />}
          sub={api ? `Version ${api.version ?? '?'}${api.deployed_at ? ` · Deployed ${formatDateTime(api.deployed_at)}` : ''}` : 'Unreachable'}
          icon="git-branch"
          accent={skew}
        />
      </div>
      <p className="hf-muted" style={{ marginTop: 'var(--s-4)' }}>
        The commit is what EasyPanel deployed (its <code>GIT_SHA</code>); the version is <code>package.json</code>'s, bumped when a release deserves a number. A commit link opens it on GitHub so the deployment can be compared with <code>main</code> at a glance.
      </p>
    </div>
  );
}

function Sha({ sha }: { sha: string }) {
  const url = commitUrl(sha);
  if (!url) return <span>unknown</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="hf-link hf-mono" title={sha}>
      {shortSha(sha)}
    </a>
  );
}
